import api from './api';

export interface CrmConnectionInfo {
  provider: string;
  status: string;
  instanceUrl: string;
  lastSyncAt: string | null;
  lastSyncError: string | null;
  connectedAt: string;
  isOwn: boolean;
}

export interface CrmDealSummary {
  id: string;
  externalId: string;
  name: string;
  amount: number | null;
  stageName: string | null;
  closeDate: string | null;
  account: { id: string; name: string; industry: string | null } | null;
  contactCount: number;
}

export interface CrmDealDetail {
  id: string;
  externalId: string;
  name: string;
  amount: number | null;
  stageName: string | null;
  closeDate: string | null;
  account: {
    id: string;
    name: string;
    industry: string | null;
    annualRevenue: number | null;
  } | null;
  contacts: Array<{
    id: string;
    firstName: string | null;
    lastName: string;
    title: string | null;
    department: string | null;
    email: string | null;
    role: string | null;
    isPrimary: boolean;
  }>;
}

export interface CrmPersonaPreview {
  name: string;
  jobTitle: string;
  type: string;
  agenda: string;
  typicalObjections: string;
  crmContactId?: string;
}

export interface CrmAggregateStats {
  industries: Array<{ industry: string; count: number; avgAmount: number }>;
  titles: Array<{ title: string; count: number }>;
  totalOpenDeals: number;
  avgDealSize: number | null;
}

export async function getCrmStatus(): Promise<CrmConnectionInfo[]> {
  const res = await api.get('/crm/status');
  return res.data.connections;
}

export async function initiateCrmAuth(provider: string): Promise<string> {
  const res = await api.get(`/crm/${provider}/auth`);
  return res.data.authUrl;
}

export async function disconnectCrm(provider: string): Promise<void> {
  await api.delete(`/crm/${provider}`);
}

export async function getCrmDeals(options?: {
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ deals: CrmDealSummary[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.search) params.set('search', options.search);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const res = await api.get(`/crm/deals?${params.toString()}`);
  return res.data;
}

export async function getCrmDealDetail(dealId: string): Promise<CrmDealDetail> {
  const res = await api.get(`/crm/deals/${dealId}`);
  return res.data;
}

export async function getCrmAggregateStats(): Promise<CrmAggregateStats> {
  const res = await api.get('/crm/aggregate');
  return res.data;
}

export async function generateDealPersonas(
  dealId: string,
): Promise<{ personas: CrmPersonaPreview[] }> {
  const res = await api.post('/crm/personas/generate-from-deal', { dealId });
  return res.data;
}

export async function generateAggregatePersonas(): Promise<{
  personas: CrmPersonaPreview[];
}> {
  const res = await api.post('/crm/personas/generate-aggregate');
  return res.data;
}

export async function saveCrmPersonas(
  personas: CrmPersonaPreview[],
  source: 'crm_deal' | 'crm_aggregate',
  dealId?: string,
): Promise<void> {
  await api.post('/crm/personas/save', { personas, source, dealId });
}
